import { useCallback, useEffect, useRef, useState } from 'react';

/** How long a count may run before the screen says it is counting. */
const SAY_COUNTING_AFTER_MS = 3000;
/** Documents finish and leave on their own, so an open Export screen recounts this often. */
const RECOUNT_EVERY_MS = 10_000;

export type ExportReadyCount = {
  /** Documents an Export now would send; null until the first count lands. */
  ready: number | null;
  /** Documents waiting only on the destination, per local document type id; null until counted. */
  heldByType: Record<string, number> | null;
  /** Of `ready`, the documents that go to the exceptions folder instead (ADR-0096); null until counted. */
  toExceptions: number | null;
  /** A count has been running for more than three seconds. */
  counting: boolean;
  /** Why the last count failed, or null. */
  error: string | null;
  /** Count again now (after an export, for example). */
  recount: () => void;
};

/**
 * How many documents Export now would send, for enabling the button.
 *
 * The count is the engine's own export preview, read from a private snapshot that
 * never waits on or slows scanning (DocumentStore.InspectLocalExportEligibility;
 * 0.2 s at most in a day of dev logs). The count it replaced paused whenever
 * anything was processing and restarted, discarding its answer, each time that
 * signal flickered — so during an import it never finished. This one counts at
 * once, lets every count finish, and runs a single follow-up when something
 * changed while it was counting.
 */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function useExportReadyCount(jobId: string, enabled: boolean): ExportReadyCount {
  const [ready, setReady] = useState<number | null>(null);
  const [heldByType, setHeldByType] = useState<Record<string, number> | null>(null);
  const [toExceptions, setToExceptions] = useState<number | null>(null);
  const [counting, setCounting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const again = useRef(false);
  const current = useRef({ jobId, enabled });
  current.current = { jobId, enabled };

  const count = useCallback(async () => {
    const api = window.capturePointDesktop;
    if (!api || !current.current.enabled) return;
    if (inFlight.current) {
      again.current = true;
      return;
    }
    inFlight.current = true;
    const asked = current.current.jobId;
    const slow = window.setTimeout(() => setCounting(true), SAY_COUNTING_AFTER_MS);
    try {
      const preview = await api.previewExport(asked);
      if (current.current.jobId === asked) {
        setReady(preview.eligible);
        setHeldByType(preview.destinationHeldByType ?? {});
        setToExceptions(preview.toExceptions ?? 0);
        setError(null);
      }
    } catch (reason) {
      if (current.current.jobId === asked)
        setError(reason instanceof Error ? reason.message : 'The documents could not be counted.');
    } finally {
      window.clearTimeout(slow);
      setCounting(false);
      inFlight.current = false;
      if (again.current) {
        again.current = false;
        void count();
      }
    }
  }, []);

  useEffect(() => {
    setReady(null);
    setHeldByType(null);
    setToExceptions(null);
    setError(null);
    if (!enabled) return;
    void count();
    const saved = (event: Event) => {
      if ((event as CustomEvent).detail === jobId) void count();
    };
    window.addEventListener('capturepoint-review-saved', saved);
    const timer = window.setInterval(() => void count(), RECOUNT_EVERY_MS);
    return () => {
      window.removeEventListener('capturepoint-review-saved', saved);
      window.clearInterval(timer);
    };
  }, [jobId, enabled, count]);

  return { ready, heldByType, toExceptions, counting, error, recount: () => void count() };
}
